import type {
  ApiCallSummary,
  ApiTokenSummary,
  DeviceSummary,
  PasskeySummary,
} from '../../core/auth/session.js';
import type { ApiScope } from '@nedelnik/shared';

import { LEGACY_HOSTS } from '../../core/moved.js';

/**
 * Подписи в настройках.
 *
 * Устройство и ключ человек узнаёт не по идентификатору, а по тому, когда им
 * последний раз пользовались: «Этот браузер» и «Этот браузер» отличаются
 * только этим.
 */

const DAY_MONTH = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' });

const KINDS: Record<DeviceSummary['kind'], string> = {
  web: 'браузер',
  ios: 'айфон',
  agent: 'агент',
};

/**
 * Подпись устройства.
 *
 * Дата стоит и у отключённого: «браузер · отключено» у двух строк подряд
 * означает, что различить их нельзя, — а именно за этим на список и смотрят.
 * Имя приходит с сервера из `User-Agent`; у входов, сделанных до того, как их
 * начали называть, его нет, и тогда остаётся вид устройства.
 */
export function deviceLabel(device: DeviceSummary): string {
  const name = device.name ?? KINDS[device.kind];
  const parts = [name];

  if (device.current) parts.push('это устройство');
  else if (!device.active) parts.push(`отключено, ${seenLabel(device.last_seen_at)}`);
  else parts.push(seenLabel(device.last_seen_at));

  return parts.join(' · ');
}

/**
 * Подпись, которую клиент ставил ключу до того, как имена начал ставить сервер.
 *
 * Читать её нельзя ни с какого устройства, кроме того единственного, где ключ
 * заводили: с ноутбука «Этот браузер» означает «браузер, который вы сейчас
 * видите», хотя ключ живёт на телефоне. Рядом стоит кнопка «Привязать ключ на
 * этом устройстве», и вместе они читались как прямое противоречие.
 */
const LEGACY_NAME = 'Этот браузер';

/**
 * Подпись ключа.
 *
 * «Заведён в», а не просто имя браузера: имя говорит, **где ключ заводили**, а
 * не где он лежит. Ключ из менеджера паролей или из связки ключей синхронизуется
 * на все устройства человека, и один и тот же ключ, заведённый на телефоне,
 * работает и с ноутбука. Подпись «Safari на айфоне» без этого слова означала бы
 * «ключ на айфоне» — ту же неправду, что и прежнее «Этот браузер», только
 * тише.
 */
export function passkeyLabel(passkey: PasskeySummary, host?: string): string {
  return [
    whereLabel(passkey),
    passkey.last_used_at == null
      ? 'ещё не использован'
      : `входили ${dateLabel(passkey.last_used_at)}`,
    ...(host === undefined ? [] : [host]),
  ].join(' · ');
}

/**
 * К какому адресу привязан ключ — если об этом есть что сказать (этап 27).
 *
 * Ключ пускает только на домене, где его завели, и с двумя адресами одного
 * приложения это впервые стало видно: ключ из списка не предлагается при
 * входе. Пока все ключи здешние, адрес — шум, и подписи нет. Как только есть
 * хоть один чужой, адрес получают все: «какой ключ для какого адреса» — ровно
 * тот вопрос, с которым на список смотрят.
 *
 * Чужой ключ старого адреса называется так прямо: здесь он не пустит, и его
 * можно отозвать, когда старым адресом перестанут пользоваться.
 */
export function passkeyHostLabels(
  passkeys: readonly PasskeySummary[],
  here: string,
): Map<string, string> {
  const labels = new Map<string, string>();
  if (passkeys.every((passkey) => passkey.rp_id === here)) return labels;

  for (const passkey of passkeys) {
    labels.set(
      passkey.id,
      passkey.rp_id === here
        ? passkey.rp_id
        : LEGACY_HOSTS.includes(passkey.rp_id)
          ? `для старого адреса ${passkey.rp_id}`
          : `только для ${passkey.rp_id}`,
    );
  }
  return labels;
}

/**
 * Чем подписан ключ — по убыванию того, насколько ответ полезен.
 *
 * 1. Где лежит («Bitwarden», «Пароли Apple») — это и есть ответ на вопрос,
 *    который человек задаёт списку ключей. Приходит с сервера из AAGUID.
 * 2. Где заводили («заведён в Safari на айфоне») — слабее: ключ из связки
 *    ключей или менеджера паролей работает и с других устройств.
 * 3. Когда завели — у ключей, привязанных до того, как их начали называть.
 *    Отличить один такой от другого этого хватает, а «ключ без имени» —
 *    отписка: дату-то мы знаем.
 */
function whereLabel(passkey: PasskeySummary): string {
  if (passkey.provider != null) return passkey.provider;

  const named = passkey.name != null && passkey.name !== LEGACY_NAME;
  return named ? `заведён в ${passkey.name ?? ''}` : `заведён ${dateLabel(passkey.created_at)}`;
}

function seenLabel(at: string | null): string {
  return at == null ? 'входа ещё не было' : `последний раз ${dateLabel(at)}`;
}

/**
 * Скоупы словами. «Читать, писать, удалять» — то, что человек выбирает и
 * что потом видит в строке токена; машинные `read`/`write`/`destroy` живут в
 * API и в инструкции для агента.
 */
export const SCOPE_LABELS: Record<ApiScope, string> = {
  read: 'читать',
  write: 'писать',
  destroy: 'удалять',
};

/** Что скоуп разрешает — под галочкой, потому что «удалять» шире, чем звучит. */
export const SCOPE_HINTS: Record<ApiScope, string> = {
  read: 'смотреть задачи, спринты и выгрузку',
  write: 'заводить, править, переносить и закрывать — до 20 задач за раз',
  destroy: 'удалять и стирать из корзины, импортировать, менять больше 20 задач разом',
};

/**
 * Подпись токена: имя, хвост и когда им пользовались в последний раз.
 * Хвост — единственное, что отличает два токена с одним именем, а «ещё не
 * использован» отвечает на вопрос «подключился ли агент вообще».
 */
export function tokenLabel(token: ApiTokenSummary): string {
  const parts = [`${token.name} · …${token.hint}`];
  if (token.revoked_at != null) parts.push(`отозван ${dateLabel(token.revoked_at)}`);
  else if (token.last_used_at == null) parts.push('ещё не использован');
  else parts.push(`использован ${dateLabel(token.last_used_at)}`);
  return parts.join(' · ');
}

const DAY_MONTH_TIME = new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Строка следа: время, метод и путь, номера задач. Со временем — в отличие от
 * подписей устройств: «кто закрыл Н-42» спрашивают про сегодняшний час, а не
 * про день.
 */
export function callLabel(call: ApiCallSummary): string {
  let when: string;
  try {
    when = DAY_MONTH_TIME.format(new Date(call.at));
  } catch {
    when = 'неизвестно когда';
  }
  const refs = call.refs.length > 0 ? ` · ${call.refs.join(', ')}` : '';
  const failed = call.status >= 400 ? ` · отказ ${String(call.status)}` : '';
  return `${when} · ${call.method} ${call.path}${refs}${failed}`;
}

/**
 * Дата словами. Разбор обёрнут не из перестраховки: `Intl.format` от
 * `Invalid Date` бросает `RangeError`, а в React это уносит всё дерево —
 * страница гаснет. Тот же урок записан в подписи срока и в корзине.
 */
function dateLabel(value: string): string {
  try {
    return DAY_MONTH.format(new Date(value));
  } catch {
    return 'неизвестно когда';
  }
}
